import React, { useState, useEffect } from 'react';
import { authAPI, projectsAPI } from './services/api';
import AuthLogin from './components/AuthLogin';
import AdminDashboard from './components/AdminDashboard';
import VendorPortal from './components/VendorPortal';
import GlobalHeader from './components/GlobalHeader';
import AuditLogModal from './components/AuditLogModal';
import UserManagementModal from './components/UserManagementModal';
import ProjectSelector from './components/ProjectSelector';
import ReportsPage from './components/ReportsPage';
import SystemAdminPage from './components/SystemAdminPage';
import ProjectsPage from './components/ProjectsPage';
import UsersPage from './components/UsersPage';
import Header from './components/Header';
import Footer from './components/Footer';

const App = () => {
  const [user, setUser] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAudit, setShowAudit] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showSystemAdmin, setShowSystemAdmin] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showLoginAnimation, setShowLoginAnimation] = useState(false);

  // Function to refresh user projects from server
  const refreshUserProjects = async (newProject = null) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (userData.username) {
        // Get all projects from the API
        const response = await projectsAPI.getAll();
        const updatedProjects = response.data;
        
        // Update user object with new projects
        const updatedUser = {
          ...userData,
          projects: updatedProjects
        };
        
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        
        // If a new project was just created, select it
        if (newProject) {
          const foundProject = updatedProjects.find(p => p.id === newProject.id);
          if (foundProject) {
            setCurrentProject(foundProject);
            localStorage.setItem('currentProjectId', foundProject.id.toString());
          }
        } else if (currentProject) {
          // Update current project if it still exists
          const stillExists = updatedProjects.find(p => p.id === currentProject.id);
          if (stillExists) {
            setCurrentProject(stillExists);
          } else if (updatedProjects.length > 0) {
            setCurrentProject(updatedProjects[0]);
            localStorage.setItem('currentProjectId', updatedProjects[0].id.toString());
          }
        } else if (updatedProjects.length > 0) {
          // If no current project, select the first one
          setCurrentProject(updatedProjects[0]);
          localStorage.setItem('currentProjectId', updatedProjects[0].id.toString());
        }
      }
    } catch (error) {
      console.error('Error refreshing projects:', error);
    }
  };

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const savedProjectId = localStorage.getItem('currentProjectId');
    
    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Set current project if available
      if (parsedUser.projects && parsedUser.projects.length > 0) {
        if (savedProjectId) {
          const project = parsedUser.projects.find(p => p.id === parseInt(savedProjectId));
          if (project) {
            setCurrentProject(project);
            localStorage.setItem('currentProjectId', project.id.toString());
          } else if (parsedUser.projects.length === 1) {
            // Auto-select if only one project
            setCurrentProject(parsedUser.projects[0]);
            localStorage.setItem('currentProjectId', parsedUser.projects[0].id.toString());
          }
        } else if (parsedUser.projects.length === 1) {
          // Auto-select if only one project
          setCurrentProject(parsedUser.projects[0]);
          localStorage.setItem('currentProjectId', parsedUser.projects[0].id.toString());
        }
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Set user data immediately but show animation
      setUser(userData);
      setShowLoginAnimation(true);
      
      // Auto-select project if user has only one project
      if (userData.projects && userData.projects.length === 1) {
        setCurrentProject(userData.projects[0]);
        localStorage.setItem('currentProjectId', userData.projects[0].id.toString());
      } else if (userData.role === 'superadmin' && userData.projects && userData.projects.length > 0) {
        // For superadmin, select first project by default
        setCurrentProject(userData.projects[0]);
        localStorage.setItem('currentProjectId', userData.projects[0].id.toString());
      }
      
      // Hide animation after 2 seconds
      setTimeout(() => {
        setShowLoginAnimation(false);
      }, 2000);
      
      return { success: true };
    } catch (error) {
      setShowLoginAnimation(false);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const handleProjectChange = (project) => {
    setCurrentProject(project);
    localStorage.setItem('currentProjectId', project.id.toString());
    // Force a re-render by updating state - no need to reload the page
    // The AdminDashboard will automatically fetch new data when currentProject changes
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentProjectId');
    setUser(null);
    setCurrentProject(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500"></div>
        <p className="ml-3 text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <AuthLogin onLogin={handleLogin} />;
  }
  
  // Show login animation - must be checked after user is set
  if (showLoginAnimation && user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
        <div className="animate-bounce">
          <div className="w-32 h-32 bg-white rounded-2xl flex items-center justify-center shadow-2xl">
            <span className="text-4xl font-bold text-indigo-600">SOS</span>
          </div>
        </div>
        <p className="mt-8 text-white text-xl font-semibold animate-pulse">جاري التحميل...</p>
      </div>
    );
  }
  
  // Show projects page if requested - check early, before project selection
  if (showProjects && user) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <Header />
        <div className="flex-1">
          <ProjectsPage 
            onClose={() => setShowProjects(false)}
            onProjectCreated={refreshUserProjects}
            onProjectDeleted={async (deletedProjectId) => {
              await refreshUserProjects();
              if (currentProject && currentProject.id === deletedProjectId) {
                const updatedProjects = user?.projects?.filter(p => p.id !== deletedProjectId) || [];
                if (updatedProjects.length > 0) {
                  setCurrentProject(updatedProjects[0]);
                  localStorage.setItem('currentProjectId', updatedProjects[0].id.toString());
                } else {
                  setCurrentProject(null);
                  localStorage.removeItem('currentProjectId');
                }
              }
            }}
          />
        </div>
        <Footer />
      </div>
    );
  }

  // Show users page if requested - check early, before project selection
  if (showUsers && user) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <Header />
        <div className="flex-1">
          <UsersPage onClose={() => setShowUsers(false)} />
        </div>
        <Footer />
      </div>
    );
  }
  
  // Show reports page if requested - check early, before project selection
  if (showReports && user) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <Header />
        <div className="flex-1">
          <ReportsPage onClose={() => setShowReports(false)} />
        </div>
        <Footer />
      </div>
    );
  }

  // Show project selector if user has projects but none selected, or if superadmin
  const needsProjectSelection = user.projects && user.projects.length > 0 && !currentProject;
  const canProceed = currentProject || (user.role === 'superadmin' && (!user.projects || user.projects.length === 0));

  if (needsProjectSelection) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <ProjectSelector 
          projects={user.projects} 
          onSelect={handleProjectChange}
          userRole={user.role}
        />
      </div>
    );
  }

  if (!canProceed) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">No Projects Available</h2>
          <p className="text-gray-600">You have not been assigned to any projects. Please contact your administrator.</p>
          <button
            onClick={handleLogout}
            className="mt-6 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  // Don't show currentView if showing reports or system admin (handled separately)
  // Manager role only sees reports page
  const currentView = user.role === 'vendor' 
    ? <VendorPortal vendorId={user.username} />
    : user.role === 'manager'
      ? null // Manager will only see reports page
      : <AdminDashboard userId={user.username} role={user.role} currentProject={currentProject} projects={user.projects} />;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Header />
      <GlobalHeader 
        role={user.role} 
        vendorId={user.username} 
        currentProject={currentProject}
        projects={user.projects}
        onProjectChange={handleProjectChange}
        onLogout={handleLogout}
        onOpenAudit={() => setShowAudit(true)} 
        onOpenUserManagement={() => setShowUsers(true)}
        onOpenProjects={() => setShowProjects(true)}
        onOpenReports={() => setShowReports(true)}
      />
      <main className="flex-1">
        {user.role === 'manager' ? (
          <ReportsPage onClose={() => {}} />
        ) : (
          currentView
        )}
      </main>
      <Footer />
      
      {/* Modals */}
      {showAudit && <AuditLogModal onClose={() => setShowAudit(false)} />}
      {showUserManagement && (
        <UserManagementModal 
          onClose={() => setShowUserManagement(false)} 
          onProjectCreated={refreshUserProjects}
          onProjectDeleted={async (deletedProjectId) => {
            // Refresh projects list when a project is deleted
            await refreshUserProjects();
            // If the deleted project was the current project, switch to another one
            if (currentProject && currentProject.id === deletedProjectId) {
              const updatedProjects = user?.projects?.filter(p => p.id !== deletedProjectId) || [];
              if (updatedProjects.length > 0) {
                setCurrentProject(updatedProjects[0]);
                localStorage.setItem('currentProjectId', updatedProjects[0].id.toString());
              } else {
                setCurrentProject(null);
                localStorage.removeItem('currentProjectId');
              }
            }
          }}
        />
      )}
    </div>
  );
};

export default App;
